import { GridColDef } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { useUnitListStore } from "../useUnitListStore";
import { Chip } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { UnitStatusLabels, UnitStatusFilters, UnitDataForUnitListDashboard } from "@safelease/service-utilities";
import { Formatters } from "@safelease/service-utilities";
import { Image } from "../../../image";

export const useColumnDefs = () => {
  const { selectedTab } = useUnitListStore((state) => ({
    selectedTab: state.selectedTab,
  }));

  const baseColumns: GridColDef<UnitDataForUnitListDashboard>[] = [
    {
      headerName: "Location",
      field: "fullAddress",
      flex: 1,
    },
    {
      headerName: "Unit",
      field: "unitName",
      flex: 1,
    },
    {
      headerName: "Tenant",
      field: "tenantName",
      flex: 1,
    },
    {
      headerName: "Move In",
      field: "startDate",
      valueFormatter: ({ value }) => {
        return value ? dayjs(value).format("MM/DD/YY") : "";
      },
      flex: 1,
    },
    {
      headerName: "Status",
      field: "status",
      renderCell: ({ row }) => {
        return getStatusChip(row.status, "unitList");
      },
      flex: 1,
    },
  ];

  if (selectedTab === UnitStatusFilters.SAFELEASE || selectedTab === UnitStatusFilters.SAFELEASE_PENDING_OPT_OUT) {
    baseColumns.push({
      headerName: "Prem/Cov",
      field: "policyPremiumCoverage",
      valueGetter: ({ row }) => {
        if (!row.policyPremiumCents || !row.policyCoverageCents) return "N/A";
        return `${Formatters.penniesToDollars(row.policyPremiumCents)} / ${Formatters.penniesToDollars(row.policyCoverageCents)}`;
      },
      flex: 1,
    });
    baseColumns.push({
      headerName: "Enrollment date",
      field: "policyStartDate",
      valueGetter: ({ row }) => {
        if (!row.policyStartDate) return "N/A";
        return dayjs(row.policyStartDate).format("MM/DD/YY");
      },
    });
  } else if (selectedTab === UnitStatusFilters.ELIGIBLE) {
    baseColumns.push({
      headerName: "Opportunity",
      field: "opportunity",
      flex: 1,
    });
  }

  return baseColumns;
};

export const getStatusChip = (status: UnitStatusLabels, destinationComponent: "detailsPage" | "unitList") => {
  switch (status) {
    case UnitStatusLabels.SAFELEASE:
      if (destinationComponent === "unitList") {
        return (
          <Chip
            sx={{ "& .MuiChip-label": { fontWeight: 400 } }}
            label="SafeLease"
            avatar={<Image alt="SafeLease logo" src="/images/logos/icon/safelease_logoicon_rgb_blueonwhite.svg" />}
            size="small"
            variant="outlined"
          />
        );
      } else {
        return (
          <Chip
            label="SafeLease"
            sx={{ backgroundColor: "#E6F0FF", color: "#05337E", "& .MuiChip-label": { fontWeight: 400 } }}
            size="small"
            icon={<CheckCircle color="primary" />}
          />
        );
      }
    case UnitStatusLabels.OPTED_OUT:
      return <Chip label="Opted-Out" size="small" sx={{ "& .MuiChip-label": { fontWeight: 400 } }} />;
    case UnitStatusLabels.ELIGIBLE:
      return (
        <Chip label="Eligible" size="small" sx={{ backgroundColor: "#15B79F", color: "white", "& .MuiChip-label": { fontWeight: 400 } }} />
      );
    case UnitStatusLabels.ELIGIBLE_GRACE:
    case UnitStatusLabels.ELIGIBLE_PENDING:
      return (
        <Chip
          label="Eligible"
          size="small"
          variant="outlined"
          sx={{
            color: "#15B79F",
            borderColor: "#15B79F",
            "& .MuiChip-label": {
              color: "#15B79F",
              fontWeight: 400,
            },
          }}
        />
      );
    case UnitStatusLabels.SAFELEASE_PENDING_OPT_OUT:
      return (
        <Chip
          label="Pending opt-out"
          size="small"
          sx={{
            backgroundColor: "#FEF3C7",
            color: "black",
            "& .MuiChip-label": { fontWeight: 400 },
          }}
        />
      );
    case UnitStatusLabels.INELIGIBLE:
      return (
        <Chip
          label="Ineligible"
          size="small"
          sx={{ backgroundColor: "#212636", color: "white", "& .MuiChip-label": { fontWeight: 400 } }}
        />
      );
    default:
      return <Chip label="Unknown" />;
  }
};
